import { useState } from 'react'
import {
  useAcknowledgeFieldMessage,
  useCloseFieldMessage,
  useMyFieldMessages,
  useOnWayFieldMessage
} from '../api/queries'
import type { FieldMessage } from '../api/types'
import { AddressLink, PhoneLink } from './SiteContact'

/**
 * Det, montøren ser øverst i appen, når der er sendt noget til ham
 * (beslutning 060).
 *
 * <p>
 * Et akut udkald skal ikke kunne overses - derfor rødt, øverst, og uden at kunne
 * lukkes væk. Det forsvinder, når han har afsluttet det, ikke når han har set
 * det: hele pointen er, at kontoret kan se, hvor det står.
 * </p>
 *
 * En besked fra kontoret er blå og roligere - den lægger sig oven i det, han
 * står med, frem for at rive ham ud af det.
 */
export function FieldMessageBanner() {
  const { data } = useMyFieldMessages()
  const messages = data ?? []

  if (messages.length === 0) return null

  return (
    <div className="stack" style={{ gap: 8, marginBottom: 12 }}>
      {messages.map((m) => (
        <MessageCard key={m.id} message={m} />
      ))}
    </div>
  )
}

function MessageCard({ message }: { message: FieldMessage }) {
  const acknowledge = useAcknowledgeFieldMessage()
  const onWay = useOnWayFieldMessage()
  const close = useCloseFieldMessage()
  const [outcome, setOutcome] = useState('')
  const [closing, setClosing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const busy = acknowledge.isPending || onWay.isPending || close.isPending

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    }
  }

  return (
    <div
      style={{
        borderRadius: 'var(--radius)',
        border: `2px solid ${message.kindColor}`,
        background: message.isUrgent ? '#FEF2F2' : '#F0F9FF',
        padding: 12
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: message.kindColor }}>
          {message.isUrgent ? '🚨 AKUT UDKALD' : '🔔 Besked fra kontoret'}
        </span>
        <span className="tiny muted">
          {message.sentBy ? `fra ${message.sentBy.displayName}` : ''} ·{' '}
          {message.statusName.toLowerCase()}
        </span>
      </div>

      <div style={{ fontWeight: 600, fontSize: 15, marginTop: 4 }}>{message.title}</div>

      {message.description && <div className="small">{message.description}</div>}

      {(message.customerName || message.projectName) && (
        <div className="small muted">
          {message.customerName}
          {message.projectName && ` · ${message.projectName}`}
          {message.deviceName && ` · ${message.deviceName}`}
        </div>
      )}

      {/* Adressen og nummeret er det, han skal bruge i bilen - derfor som
          tryk-for-at-ringe og kortopslag (beslutning 025). */}
      {message.site && (
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
          <AddressLink contact={message.site} />
          {message.site.phone && (
            <span className="small">
              {message.site.contactPerson ?? 'Kunden'}: <PhoneLink phone={message.site.phone} />
            </span>
          )}
        </div>
      )}

      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}

      <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {message.status === 'sent' && (
          <button
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => void run(() => acknowledge.mutateAsync(message.id))}
          >
            Jeg har set den
          </button>
        )}

        {message.isUrgent && message.status !== 'on_way' && (
          <button
            className="btn btn--sm"
            disabled={busy}
            onClick={() => void run(() => onWay.mutateAsync(message.id))}
          >
            Jeg er på vej
          </button>
        )}

        {!closing ? (
          <button className="btn btn--sm" disabled={busy} onClick={() => setClosing(true)}>
            Afslut
          </button>
        ) : (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', flex: 1 }}>
            <input
              value={outcome}
              placeholder="Hvad blev der gjort?"
              style={{ flex: 1, minWidth: 160 }}
              onChange={(e) => setOutcome(e.target.value)}
            />
            <button
              className="btn btn--primary btn--sm"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  close.mutateAsync({ id: message.id, outcome: outcome.trim() || null })
                )
              }
            >
              Gem
            </button>
            <button className="btn btn--sm" onClick={() => setClosing(false)}>
              Fortryd
            </button>
          </div>
        )}
      </div>

      {message.isUrgent && message.status === 'sent' && (
        <div className="tiny muted" style={{ marginTop: 6 }}>
          Kontoret kan se, om du har set udkaldet. Haster det, ringer de - systemet erstatter ikke
          et opkald.
        </div>
      )}
    </div>
  )
}
